import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import HomePage from './pages/HomePage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Login from './pages/Login';
import ProtectedRoute from './components/ProtectedRoute';
import ControlCenterLayout from './components/control-center/Layout';
import Dashboard from './pages/control-center/Dashboard';
import Emails from './pages/control-center/Emails';
import Clients from './pages/control-center/Clients';
import Insights from './pages/control-center/Insights';
import Errors from './pages/control-center/Errors';
import Settings from './pages/control-center/Settings';
import { AuthProvider } from './context/AuthContext';

// Enable Lenis smooth scrolling only on the public site AND only on
// non-touch / fine-pointer devices. iOS/Android native scroll already
// has GPU-accelerated momentum; running Lenis on top of it causes jank.
const LenisPublic = () => {
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/control-center') || path.startsWith('/login')) return;
    // Skip on touch / coarse-pointer devices (mobile, tablets)
    const isTouch =
      (typeof window !== 'undefined') &&
      (window.matchMedia?.('(pointer: coarse)')?.matches ||
       'ontouchstart' in window ||
       (navigator.maxTouchPoints || 0) > 0);
    if (isTouch) return;
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          {/* Respect the user's OS-level reduced-motion setting globally */}
          <MotionConfig reducedMotion="user">
            <LenisPublic />
            <Routes>
              {/* Public */}
              <Route path="/" element={<HomePage />} />
              <Route path="/privacidad" element={<PrivacyPolicy />} />
              <Route path="/login" element={<Login />} />

              {/* Control Center — protected */}
              <Route path="/control-center" element={<ProtectedRoute><ControlCenterLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/control-center/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="emails" element={<Emails />} />
                <Route path="clients" element={<Clients />} />
                <Route path="insights" element={<Insights />} />
                <Route path="errors" element={<Errors />} />
                <Route path="settings" element={<Settings />} />
              </Route>
            </Routes>
          </MotionConfig>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
